import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, InputAdornment, TextField, Typography } from '@mui/material';
import { Add, Search } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DataTable } from '../../components/tables/DataTable';
import { Pagination, PAGINATION_BAR_HEIGHT } from '../../components/tables/Pagination';
import { ConfirmDialog, CONFIRM_INITIAL, type ConfirmDialogState } from '../../components/shared/ConfirmDialog';
import { PAGINATION_DEFAULT_PAGE_SIZE } from '../../constants/config';
import { ROUTES } from '../../constants/routes';
import { getClients } from '../../services/clientService';
import { deleteProject, getProjects, getStatusCounts } from '../../services/projectService';
import { getSites } from '../../services/siteService';
import type { Client, Project, Site } from '../../types/project';
import { projectsColumns } from './projectsColumns';
import { ProjectFilters, type ProjectFilterValues } from './ProjectFilters';
import { NewProjectDialog } from './NewProjectDialog';
import { EditProjectDialog } from './EditProjectDialog';
import { ColumnSelector } from '../../components/tables/ColumnSelector';
import { useColumnPreferences } from '../../hooks/useColumnPreferences';
import { usePermissions } from '../../hooks/usePermissions';

const DEFAULT_PROJECT_FIELDS = projectsColumns.filter((c) => c.field !== 'ApprovalStatus').map((c) => c.field);
const MANDATORY_PROJECT_FIELDS = ['ProjectName', 'Status'];

const DEFAULT_FILTERS: ProjectFilterValues = {
  statuses: ['Active', 'Planning'],
  clientKey: null,
  startDate: '',
  endDate: '',
};

const buildPageTitle = (statuses: string[]): string => {
  if (statuses.length === 0) return 'All Projects';
  if (statuses.length === 1) return `${statuses[0]} Projects`;
  if (statuses.length === 2 && statuses.includes('Active') && statuses.includes('Planning')) {
    return 'Active Projects';
  }
  return 'Projects';
};

export const ProjectsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [data, setData] = useState<Project[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGINATION_DEFAULT_PAGE_SIZE);
  // BUG-2026-0206: land on the projects being actively worked on — most
  // recently modified first (matches the Work Orders tab default).
  const [sortBy, setSortBy] = useState('sysLastModifiedDate');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [filters, setFilters] = useState<ProjectFilterValues>(DEFAULT_FILTERS);

  const urlSearch = searchParams.get('search') || '';
  const urlNewProject = searchParams.get('newProject');
  const [search, setSearch] = useState(urlSearch);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (urlSearch) {
      setSearch(urlSearch);
    }
  }, [urlSearch]);
  const [clients, setClients] = useState<Client[]>([]);
  const [sites, setSites] = useState<Site[]>([]);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [dialogOpen, setDialogOpen] = useState(urlNewProject === '1');
  const [editProject, setEditProject] = useState<Project | null>(null);
  const [confirmDlg, setConfirmDlg] = useState<ConfirmDialogState>(CONFIRM_INITIAL);

  useEffect(() => {
    getClients().then(setClients).catch(() => {});
    getSites().then(setSites).catch(() => {});
    getStatusCounts().then(setStatusCounts).catch(() => {});
  }, []);

  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = filters.statuses.length > 0 ? filters.statuses.join(',') : undefined;
      const result = await getProjects({
        page,
        pageSize,
        sortBy,
        sortDir,
        status: statusParam,
        clientKey: filters.clientKey ?? undefined,
        startDate: filters.startDate || undefined,
        endDate: filters.endDate || undefined,
        search: search || undefined,
      });
      setData(result.data);
      setTotal(result.total);
    } catch {
      setData([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, sortBy, sortDir, filters, search]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
  };

  const handleRowClick = (row: Record<string, unknown>) => {
    const projectId = row.ProjectId as string;
    navigate(ROUTES.LOCATIONS.replace(':projectId', projectId));
  };

  const handleFilterChange = (next: ProjectFilterValues) => {
    setFilters(next);
    setPage(0);
  };

  const handleSearchChange = (value: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setSearch(value);
      setPage(0);
    }, 300);
  };

  const handleClearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(0);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setPage(0);
  };

  const handleProjectCreated = (project: Project) => {
    setDialogOpen(false);
    navigate(ROUTES.LOCATIONS.replace(':projectId', project.ProjectId));
  };

  const handleEditClick = (row: Record<string, unknown>) => {
    const proj = data.find((p) => p.ProjectKey === row.ProjectKey) ?? null;
    setEditProject(proj);
  };

  const handleProjectUpdated = () => {
    setEditProject(null);
    fetchProjects();
  };

  const handleDeleteClick = (row: Record<string, unknown>) => {
    const id = row.ProjectId as string;
    setConfirmDlg({
      open: true,
      title: 'Delete Project',
      message: `Delete project "${id}"? This action uses soft-delete and can be reversed.`,
      confirmLabel: 'Delete',
      confirmColor: 'error',
      onConfirm: async () => {
        try {
          await deleteProject(row.ProjectKey as string);
          fetchProjects();
        } catch {
          setConfirmDlg({
            open: true,
            title: 'Error',
            message: 'Failed to delete project. It may have active work orders.',
            confirmLabel: 'OK',
            confirmColor: 'primary',
            onConfirm: () => {},
          });
        }
      },
    });
  };

  const { canManageEntities } = usePermissions();
  const { visibleFields: projVisibleFields, columnVisibility: projColVis, toggleColumn: projToggle, resetDefaults: projReset } =
    useColumnPreferences('projects', projectsColumns, DEFAULT_PROJECT_FIELDS, MANDATORY_PROJECT_FIELDS);
  const filteredProjectColumns = projectsColumns.filter((c) => projVisibleFields.includes(c.field));

  const pageTitle = useMemo(() => buildPageTitle(filters.statuses), [filters.statuses]);

  const tableData = data.map((p) => {
    const client = clients.find((c) => c.ClientKey === p.ClientKey);
    const site = sites.find((s) => s.SiteKey === p.SiteKey);
    return {
      ...p,
      SiteName: site?.SiteName ?? '',
      ClientName: client?.ClientName ?? '',
      SiteCity: site?.SiteCity ?? '',
    };
  });

  return (
    <Box sx={{ pt: 1, pb: 3 }}>
      {/* Header row */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: 'text.primary',
            letterSpacing: '-0.025em',
            fontSize: '1.75rem',
            lineHeight: 1.2,
          }}
        >
          {pageTitle}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <TextField
          size="small"
          placeholder="Search projects..."
          defaultValue={urlSearch}
          onChange={(e) => handleSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 240,
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              fontSize: '0.8125rem',
            },
          }}
        />
        <ColumnSelector columns={projColVis} onToggle={projToggle} onReset={projReset} />
        {canManageEntities && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setDialogOpen(true)}
            size="small"
            sx={{
              boxShadow: '0 2px 8px rgba(74,103,65,0.2)',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 600,
              px: 2.5,
              py: 0.75,
              fontSize: '0.8125rem',
              letterSpacing: '-0.005em',
              transition: 'all 0.2s ease',
              '&:hover': {
                boxShadow: '0 4px 14px rgba(74,103,65,0.3)',
                transform: 'translateY(-1px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            New Project
          </Button>
        )}
      </Box>

      {/* Content: filters + table */}
      <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
        <ProjectFilters
          clients={clients}
          filters={filters}
          onFilterChange={handleFilterChange}
          onClear={handleClearFilters}
          statusCounts={statusCounts}
        />

        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <DataTable
            columns={filteredProjectColumns}
            data={tableData}
            loading={loading}
            onRowClick={handleRowClick}
            onEditClick={handleEditClick}
            onDeleteClick={canManageEntities ? handleDeleteClick : undefined}
            sortBy={sortBy}
            sortDir={sortDir}
            onSort={handleSort}
            stickyHeader
            bottomReserve={PAGINATION_BAR_HEIGHT}
          />

          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
          />
        </Box>
      </Box>

      <NewProjectDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={handleProjectCreated}
        initialClients={clients}
        initialSites={sites}
      />

      <EditProjectDialog
        open={!!editProject}
        project={editProject}
        onClose={() => setEditProject(null)}
        onUpdated={handleProjectUpdated}
      />
      <ConfirmDialog state={confirmDlg} onClose={() => setConfirmDlg(CONFIRM_INITIAL)} />
    </Box>
  );
};

export default ProjectsPage;
